package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.SuccessResponse
import com.sddamico.api.apiGet
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.*
import com.sddamico.models.*
import com.sddamico.state.LocalAuthActions
import com.sddamico.state.LocalAuthState
import com.sddamico.util.formatPoints
import com.sddamico.util.sportDisplayName
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

val ClubhouseGridStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(1.cssRem)
            .fillMaxWidth()
    }
    Breakpoint.MD {
        Modifier
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "repeat(2, 1fr)")
            }
            .gap(1.cssRem)
    }
}

val BreakdownCardsStyle = CssStyle {
    base {
        Modifier
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "repeat(2, 1fr)")
            }
            .gap(0.75.cssRem)
            .fillMaxWidth()
    }
    Breakpoint.MD {
        Modifier
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "repeat(8, 1fr)")
                property("align-items", "stretch")
            }
            .gap(0.75.cssRem)
            .fillMaxWidth()
    }
}

@InitRoute
fun initClubhouse(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("My Clubhouse", wideContent = true))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun ClubhousePage() {
    val authState = LocalAuthState.current
    val actions = LocalAuthActions.current
    val scope = rememberCoroutineScope()
    val ctx = rememberPageContext()
    val queryClubId = ctx.route.params["clubId"]
    val isImpersonating = authState.user?.isImpersonating == true

    // "My Roster" mode (no clubId param): require auth
    if (queryClubId == null) {
        LaunchedEffect(authState.loading, authState.user) {
            if (!authState.loading && authState.user == null) {
                ctx.router.tryRoutingTo("/sign-in?returnTo=/clubhouse")
            }
        }

        if (authState.loading || authState.user == null) {
            PageLoading()
            return
        }
    }

    val clubId = queryClubId ?: authState.user?.clubId

    if (clubId == null) {
        EmptyState("You're not on a team yet")
        return
    }

    // Keyed on the club so moving between clubhouses (only the query param changes) starts from a fresh load
    // instead of showing the previous club until the new one arrives.
    var roster by remember(clubId) { mutableStateOf<ClubRosterResponse?>(null) }
    var leagueYearId by remember(clubId) { mutableStateOf<String?>(null) }
    var pendingClaims by remember(clubId) { mutableStateOf<List<PlayerClaimResponse>>(emptyList()) }
    var pendingTrades by remember(clubId) { mutableStateOf<List<TradeOfferResponse>>(emptyList()) }
    var loading by remember(clubId) { mutableStateOf(true) }
    var claimsLoaded by remember(clubId) { mutableStateOf(false) }
    var tradesLoaded by remember(clubId) { mutableStateOf(false) }
    var error by remember(clubId) { mutableStateOf<String?>(null) }
    var claimsRefreshKey by remember { mutableStateOf(0) }
    var tradesRefreshKey by remember { mutableStateOf(0) }
    var rosterRefreshKey by remember { mutableStateOf(0) }
    var activatingLivPlayer by remember { mutableStateOf<RosterPlayerResponse?>(null) }
    var deactivatingLivPlayer by remember { mutableStateOf<RosterPlayerResponse?>(null) }

    val userId = authState.user?.id
    LaunchedEffect(clubId, rosterRefreshKey, userId) {
        try {
            // First get the league year id
            val leagueYear = apiGet("/api/leagueyears/detail", LeagueYearDetailResponse.serializer())
            leagueYearId = leagueYear.id
            roster = apiGet(
                "/api/clubs/roster?clubId=$clubId&leagueYearId=${leagueYear.id}",
                ClubRosterResponse.serializer()
            )
        } catch (e: ApiException) {
            error = e.errorMessage
        } catch (e: Exception) {
            error = "Failed to load roster"
        }
        loading = false
    }

    // Fetch pending claims
    LaunchedEffect(clubId, claimsRefreshKey, userId) {
        try {
            val resp = apiGet("/api/claims/my-pending?clubId=$clubId", PlayerClaimListResponse.serializer())
            pendingClaims = resp.claims
        } catch (_: Exception) {
            pendingClaims = emptyList()
        }
        claimsLoaded = true
    }

    // Fetch pending trades (only for own roster)
    val isOwnRoster = queryClubId == null || queryClubId == authState.user?.clubId
    LaunchedEffect(clubId, tradesRefreshKey, userId) {
        if (isOwnRoster) {
            try {
                val resp = apiGet("/api/trades/pending?clubId=$clubId", TradeOfferListResponse.serializer())
                pendingTrades = resp.trades
            } catch (_: Exception) {
                pendingTrades = emptyList()
            }
        }
        tradesLoaded = true
    }

    // Claims and trades sit above the roster, so revealing before they arrive would shove the roster down.
    if (loading || !claimsLoaded || !tradesLoaded) {
        PageLoading()
        return
    }

    if (error != null) {
        ErrorCard(error!!)
        return
    }

    val data = roster ?: return

    LaunchedEffect(data.clubName) {
        document.title = "FCC - ${data.clubName} Clubhouse"
    }

    // Header
    Column(Modifier.fillMaxWidth().gap(2.cssRem)) {
        Column {
            SpanText(
                data.clubName,
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(2.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )
            if (data.ownerName.isNotEmpty()) {
                Row(Modifier.margin(top = 0.25.cssRem)) {
                    SpanText(
                        "Owner: ",
                        Modifier.fontSize(0.875.cssRem).color(SitePalette.textSecondary)
                    )
                    SpanText(
                        data.ownerName,
                        Modifier.fontSize(0.875.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.text)
                    )
                }
            }
            // "View as" button for LEAGUE_ADMIN viewing another club
            if (!isOwnRoster && authState.user?.role == "LEAGUE_ADMIN" && !isImpersonating) {
                Div(
                    Modifier
                        .backgroundColor(SitePalette.primary)
                        .color(com.varabyte.kobweb.compose.ui.graphics.Colors.White)
                        .borderRadius(6.px)
                        .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
                        .fontSize(0.75.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .cursor(Cursor.Pointer)
                        .margin(top = 0.5.cssRem)
                        .onClick {
                            scope.launch {
                                try {
                                    val body = apiJson.encodeToString(ImpersonateRequest(clubId))
                                    apiPost("/api/admin/impersonate", body, SuccessResponse.serializer())
                                    actions.refresh()
                                    ctx.router.tryRoutingTo("/clubhouse")
                                } catch (_: Exception) {}
                            }
                        }
                        .toAttrs()
                ) {
                    SpanText("View as ${data.ownerName}")
                }
            }
        }

        // Cross-sport scoring breakdown cards
        Div(BreakdownCardsStyle.toAttrs()) {
            // Overall Rank card (gold)
            BreakdownCard(
                label = "OVERALL RANK",
                rank = data.overallRank,
                points = null,
                isAccent = true,
                icon = "\uD83C\uDFC5",
            )
            // Sport cards
            BreakdownCard(
                label = "MEN'S GOLF",
                rank = data.sportRanks["MENS_GOLF"],
                points = data.sportPoints["MENS_GOLF"],
            )
            BreakdownCard(
                label = "MEN'S TENNIS",
                rank = data.sportRanks["MENS_TENNIS"],
                points = data.sportPoints["MENS_TENNIS"],
            )
            BreakdownCard(
                label = "WOMEN'S TENNIS",
                rank = data.sportRanks["WOMENS_TENNIS"],
                points = data.sportPoints["WOMENS_TENNIS"],
            )
        }

        // Pending claims section
        if (pendingClaims.isNotEmpty()) {
            Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                SpanText(
                    "Pending Claims",
                    Modifier
                        .fontFamily(*HeadingFontFamily)
                        .fontSize(1.25.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(SitePalette.text)
                )
                pendingClaims.forEach { claim ->
                    ClaimCard(
                        claim = claim,
                        isOwnRoster = isOwnRoster && !isImpersonating,
                        onRefresh = { claimsRefreshKey++ },
                    )
                }
            }
        }

        // Pending trades
        if (pendingTrades.isNotEmpty() && isOwnRoster) {
            Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                SpanText(
                    "Pending Trades",
                    Modifier
                        .fontFamily(*HeadingFontFamily)
                        .fontSize(1.25.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(SitePalette.text)
                )
                pendingTrades.forEach { trade ->
                    TradeOfferCard(
                        trade = trade,
                        userClubId = clubId,
                        leagueYearId = leagueYearId,
                        onRefresh = { tradesRefreshKey++ },
                        onRosterRefresh = { rosterRefreshKey++ },
                        readOnly = isImpersonating,
                    )
                }
            }
        }

        // Sport sections
        val sportGroups = mapOf(
            "MENS_GOLF" to data.players.filter { it.sport == "MENS_GOLF" },
            "MENS_TENNIS" to data.players.filter { it.sport == "MENS_TENNIS" },
            "WOMENS_TENNIS" to data.players.filter { it.sport == "WOMENS_TENNIS" },
        )

        val livLineEmpty = data.players.none { it.rosterSlot == "LIV_LINE" }

        val isOtherClub = !isOwnRoster && authState.user != null
        sportGroups.forEach { (sport, players) ->
            if (players.isNotEmpty()) {
                SportSection(
                    sport, players, data.sportRanks[sport], data.sportPoints[sport], leagueYearId!!,
                    onActivateLiv = if (isOwnRoster && !isImpersonating) { player -> activatingLivPlayer = player } else null,
                    onDeactivateLiv = if (isOwnRoster && livLineEmpty && !isImpersonating) { player -> deactivatingLivPlayer = player } else null,
                    onTrade = if (isOtherClub && !isImpersonating) { player ->
                        ctx.router.tryRoutingTo("/trade?receiverClubId=$clubId&playerId=${player.id}")
                    } else null,
                )
            }
        }

        // Former players section
        if (data.formerPlayers.isNotEmpty()) {
            Column(Modifier.fillMaxWidth()) {
                SpanText(
                    "Former Players",
                    Modifier
                        .fontFamily(*HeadingFontFamily)
                        .fontSize(1.25.cssRem)
                        .color(SitePalette.textMuted)
                        .margin(bottom = 0.75.cssRem)
                )
                Div(ClubhouseGridStyle.toAttrs()) {
                    data.formerPlayers.forEach { player ->
                        FormerPlayerCard(player)
                    }
                }
            }
        }

        // Draft picks section
        if (data.draftPicks.isNotEmpty()) {
            DraftPicksSection(data.draftPicks)
        }

        // LIV Activate modal
        val livPlayer = activatingLivPlayer
        if (livPlayer != null) {
            val activeGolfers = data.players.filter { it.sport == "MENS_GOLF" && it.rosterSlot != "LIV_LINE" }
            val golfSlots = listOf("GOLFER_1", "GOLFER_2", "GOLFER_3", "GOLFER_4", "GOLFER_5")
            val occupiedSlots = activeGolfers.map { it.rosterSlot }.toSet()
            val openGolfSlots = golfSlots.filter { it !in occupiedSlots }
            LivActivateModal(
                livPlayer = livPlayer,
                activeGolfers = activeGolfers,
                openGolfSlots = openGolfSlots,
                clubId = clubId,
                onClose = { activatingLivPlayer = null },
                onSuccess = { activatingLivPlayer = null; rosterRefreshKey++ },
            )
        }

        // LIV Deactivate modal
        val deactivatePlayer = deactivatingLivPlayer
        if (deactivatePlayer != null) {
            LivDeactivateModal(
                player = deactivatePlayer,
                clubId = clubId,
                onClose = { deactivatingLivPlayer = null },
                onSuccess = { deactivatingLivPlayer = null; rosterRefreshKey++ },
            )
        }
    }
}

@Composable
private fun BreakdownCard(
    label: String,
    rank: Int?,
    points: Double?,
    isAccent: Boolean = false,
    icon: String? = null,
) {
    val bgColor = if (isAccent) SitePalette.accent else SitePalette.surface
    val textColor = if (isAccent) SitePalette.background else SitePalette.text
    val mutedColor = if (isAccent) SitePalette.background else SitePalette.textMuted

    Column(
        Modifier
            .backgroundColor(bgColor)
            .then(if (!isAccent) Modifier.border(1.px, LineStyle.Solid, SitePalette.border) else Modifier)
            .borderRadius(8.px)
            .padding(0.5.cssRem, 0.75.cssRem)
            .styleModifier { property("justify-content", "space-between") }
            .fillMaxHeight()
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            if (icon != null) {
                SpanText(
                    icon,
                    Modifier.fontSize(0.75.cssRem).color(mutedColor).margin(right = 0.25.cssRem)
                )
            }
            SpanText(
                label,
                Modifier
                    .fontSize(0.625.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .color(mutedColor)
                    .styleModifier { property("letter-spacing", "0.05em") }
            )
        }
        Row(
            Modifier.fillMaxWidth().margin(top = 0.125.cssRem),
            verticalAlignment = Alignment.Bottom
        ) {
            SpanText(
                "#${rank ?: "-"}",
                Modifier
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(textColor)
            )
            if (points != null) {
                Spacer()
                SpanText(
                    "${formatPoints(points)} pts",
                    Modifier
                        .fontSize(0.8125.cssRem)
                        .color(mutedColor)
                        .styleModifier { property("font-variant-numeric", "tabular-nums") }
                )
            }
        }
    }
}

@Composable
private fun SportSection(
    sport: String,
    players: List<com.sddamico.models.RosterPlayerResponse>,
    rank: Int?,
    sportPoints: Double?,
    leagueYearId: String,
    onActivateLiv: ((com.sddamico.models.RosterPlayerResponse) -> Unit)? = null,
    onDeactivateLiv: ((com.sddamico.models.RosterPlayerResponse) -> Unit)? = null,
    onTrade: ((com.sddamico.models.RosterPlayerResponse) -> Unit)? = null,
) {
    Column(Modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().margin(bottom = 0.75.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                sportDisplayName(sport),
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )
            Spacer()
            Div(
                Modifier
                    .display(DisplayStyle.Flex)
                    .alignItems(AlignItems.Center)
                    .backgroundColor(SitePalette.border)
                    .borderRadius(9999.px)
                    .padding(topBottom = 0.15.cssRem, leftRight = 0.6.cssRem)
                    .toAttrs()
            ) {
                SpanText(
                    "${players.size} players",
                    Modifier.fontSize(0.6875.cssRem).color(SitePalette.textSecondary)
                )
            }
            if (rank != null) {
                SpanText(
                    "#$rank",
                    Modifier
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(SitePalette.textSecondary)
                        .margin(left = 0.75.cssRem)
                )
            }
            if (sportPoints != null) {
                SpanText(
                    "(${formatPoints(sportPoints)} pts)",
                    Modifier
                        .fontSize(0.8125.cssRem)
                        .color(SitePalette.textMuted)
                        .margin(left = 0.375.cssRem)
                        .styleModifier { property("font-variant-numeric", "tabular-nums") }
                )
            }
        }

        Div(ClubhouseGridStyle.toAttrs()) {
            players.sortedWith(compareBy<com.sddamico.models.RosterPlayerResponse> { it.rosterSlot == "LIV_LINE" }.thenByDescending { it.seasonPoints }).forEach { player ->
                PlayerCard(
                    player, leagueYearId,
                    onActivateLiv = if (player.rosterSlot == "LIV_LINE" && onActivateLiv != null) {
                        { onActivateLiv(player) }
                    } else null,
                    onDeactivateLiv = if (player.rosterSlot != "LIV_LINE" && player.isLiv && player.activeTournament == null && onDeactivateLiv != null) {
                        { onDeactivateLiv(player) }
                    } else null,
                    onTrade = if (onTrade != null) {
                        { onTrade(player) }
                    } else null,
                )
            }
        }
    }
}

@Composable
private fun DraftPicksSection(picks: List<DraftPickResponse>) {
    val picksByYear = picks.groupBy { it.year }.entries.sortedBy { it.key }

    Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
        SpanText(
            "Draft Picks",
            Modifier
                .fontFamily(*HeadingFontFamily)
                .fontSize(1.25.cssRem)
                .fontWeight(FontWeight.Bold)
                .color(SitePalette.text)
        )

        picksByYear.forEach { (year, yearPicks) ->
            Column(Modifier.fillMaxWidth().gap(0.375.cssRem)) {
                SpanText(
                    "$year",
                    Modifier
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(SitePalette.textSecondary)
                )
                Div(
                    Modifier
                        .display(DisplayStyle.Flex)
                        .styleModifier { property("flex-wrap", "wrap") }
                        .gap(0.75.cssRem)
                        .fillMaxWidth()
                        .toAttrs()
                ) {
                    yearPicks.sortedBy { it.round }.forEach { pick ->
                        DraftPickCard(pick)
                    }
                }
            }
        }
    }
}

@Composable
private fun DraftPickCard(pick: DraftPickResponse) {
    val roundLabel = if (pick.round == "FIRST") "1st Round" else "2nd Round"
    Column(
        Modifier
            .styleModifier { property("min-width", "160px") }
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderRadius(8.px)
            .padding(0.625.cssRem, 0.875.cssRem)
            .gap(0.2.cssRem)
    ) {
        SpanText(
            roundLabel,
            Modifier
                .fontSize(0.875.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .color(SitePalette.accent)
        )
        if (pick.isTraded) {
            SpanText(
                "from ${pick.originalOwnerName}",
                Modifier
                    .fontSize(0.75.cssRem)
                    .color(SitePalette.textMuted)
            )
        }
    }
}

@Composable
private fun FormerPlayerCard(player: FormerPlayerResponse) {
    Row(
        Modifier
            .fillMaxWidth()
            .backgroundColor(SitePalette.surface)
            .borderRadius(0.5.cssRem)
            .padding(0.625.cssRem, 0.75.cssRem)
            .gap(0.5.cssRem),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SportBadge(player.sport)
        SpanText(
            player.name,
            Modifier.fontSize(0.875.cssRem).color(SitePalette.textSecondary)
        )
        Spacer()
        SpanText(
            formatPoints(player.seasonPoints),
            Modifier
                .fontSize(0.875.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .color(SitePalette.accent)
                .styleModifier { property("font-variant-numeric", "tabular-nums") }
        )
    }
}
